/* BMG V4 — canonical artwork configurator, metric display only.
   Shared variant IDs, prices, media and catalogue labels are never mutated. */
(function () {
  'use strict';
  const VERSION = '20260927-review-frame-sync-1';
  const INCH = /["\u2033\u201c\u201d]|\bin(?:ch(?:es)?)?\b/i;
  const numeric = value => (String(value || '').match(/\d+(?:\.\d+)?/g) || []).map(Number);
  const pretty = value => Number(value.toFixed(2)).toString();

  function createModel(data) {
    const variants = Array.isArray(data.variants) ? data.variants : [];
    const names = Array.isArray(data.optionNames) ? data.optionNames : [];
    const sizeIndex = names.findIndex(name => /size|dimension/i.test(name));
    const materialIndex = names.findIndex(name => /material|presentation|finish/i.test(name));
    const aspect = Number(data.aspect) > 0 ? Number(data.aspect) : 1;
    const rawSize = variant => String((variant.options || [])[sizeIndex >= 0 ? sizeIndex : 0] || '');
    const inchFamily = variants.some(variant => INCH.test(rawSize(variant)));
    const finish = variant => {
      const value = String(materialIndex >= 0 ? variant.options[materialIndex] : variant.title);
      if (/acrylic/i.test(value)) return 'acrylic';
      if (/\bframed\b/i.test(value) && !/unframed/i.test(value)) return 'framed';
      return 'paper';
    };
    const parsed = new Map();
    function size(variant) {
      if (parsed.has(String(variant.id))) return parsed.get(String(variant.id));
      const raw = rawSize(variant).trim();
      const numbers = numeric(raw);
      const explicitMetric = /cm|mm|centimet|millimet/i.test(raw);
      const inches = INCH.test(raw) || (!explicitMetric && inchFamily);
      const hasUnits = explicitMetric || inches;
      const factor = inches ? 2.54 : /mm|millimet/i.test(raw) ? 0.1 : 1;
      const values = numbers.map(number => number * factor);
      const long = values.length ? Math.max(...values) : 0;
      const short = values.length > 1 ? Math.min(...values) : 0;
      // This stored label was found in the catalogue audit. Do not guess 100 inches.
      const suspectCascade = data.handle === 'cascade' && inches && numbers.length === 2 && numbers.includes(10) && numbers.includes(33);
      const valid = hasUnits && values.length >= 1 && values.length <= 2 && values.every(number => number > 0) && !suspectCascade;
      const w = values.length === 1 ? (aspect >= 1 ? long : long * aspect) : (aspect >= 1 ? long : short);
      const h = values.length === 1 ? (aspect >= 1 ? long / aspect : long) : (aspect >= 1 ? short : long);
      const mismatch = values.length === 2 && short > 0 && Math.abs(Math.log((long / short) / Math.max(aspect, 1 / aspect))) > 0.12;
      const record = {
        raw, valid, inches, inferredUnits: inches && !INCH.test(raw), long,
        w, h, approximate: values.length === 1, mismatch,
        key: valid ? (values.length === 1 ? 'long:' + pretty(long) : pretty(long) + ':' + pretty(short)) : 'confirm:' + raw,
        label: valid ? (values.length === 1 ? pretty(long) : pretty(w) + ' × ' + pretty(h)) + ' cm' : 'Size to confirm'
      };
      parsed.set(String(variant.id), record);
      return record;
    }
    const groups = [];
    for (const variant of variants) {
      const detail = size(variant);
      let group = groups.find(item => item.key === detail.key);
      if (!group) { group = {key: detail.key, detail, variants: []}; groups.push(group); }
      group.variants.push(variant);
    }
    const groupOf = variant => groups.find(group => group.key === size(variant).key);
    const premiumAllowed = data.edition === 'limited' || data.edition === 'exclusive';
    const family = presentation => /^(standard|premium)$/.test(presentation) ? 'framed' : presentation;
    const blockedBySize = (variant, presentation) => size(variant).valid && size(variant).long >= 249.5 && presentation !== 'acrylic';
    function find(group, presentation, preferredId) {
      if (!group || (presentation === 'premium' && !premiumAllowed)) return null;
      const candidates = group.variants.filter(variant => variant.available && finish(variant) === family(presentation) && !blockedBySize(variant, presentation));
      return candidates.find(variant => String(variant.id) === String(preferredId)) || candidates[0] || null;
    }
    function choose(group, presentation, preferredId) {
      if (!group) return null;
      if (group.detail.valid && group.detail.long >= 249.5) return find(group, 'acrylic', preferredId);
      return find(group, presentation, preferredId) || find(group, 'acrylic', preferredId) || find(group, 'paper', preferredId) || find(group, 'standard', preferredId);
    }
    return {variants, groups, size, rawSize, groupOf, finish, premiumAllowed, blockedBySize, find, choose, aspect};
  }
  if (typeof module === 'object' && module.exports) module.exports = {createModel, VERSION};
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  if (window.BMGPrintConfigurator && window.BMGPrintConfigurator.version === VERSION) { window.BMGPrintConfigurator.init(); return; }

  function init() {
    document.querySelectorAll('[data-bmg-catalogue-product-data]').forEach(dataNode => {
      if (dataNode.dataset.ready === VERSION) return;
      const scope = dataNode.closest('.shopify-section') || document;
      const select = scope.querySelector('.bmg-product-form [name="id"]');
      if (!select) return;
      let data;
      try { data = JSON.parse(dataNode.textContent); } catch (error) { console.error('Artwork configuration could not be read.', error); return; }
      const model = createModel(data);
      if (!model.variants.length) return;
      dataNode.dataset.ready = VERSION;
      const root = scope.querySelector('[data-bmg-product-root]');
      const form = select.closest('form');
      const price = scope.querySelector('[data-bmg-price]');
      const collectorCredit = scope.querySelector('[data-bmg-collector-credit]');
      const reviewSelection = document.querySelector('[data-bmg-review-selection]');
      const reviewPrice = document.querySelector('[data-bmg-review-price]');
      const reviewCredit = document.querySelector('[data-bmg-review-credit]');
      const reviewFrame = document.querySelector('[data-bmg-review-frame]');
      const reviewScene = document.querySelector('[data-bmg-review-scene]');
      const reviewArt = document.querySelector('[data-bmg-review-art]');
      const add = document.querySelector('[data-bmg-add]');
      const wall = scope.querySelector('[data-bmg-wall]');
      const art = scope.querySelector('[data-bmg-wall-art]');
      const caption = scope.querySelector('[data-bmg-dimensions]');
      const modal = scope.querySelector('[data-bmg-wall-modal]');
      const modalScene = scope.querySelector('[data-bmg-wall-modal-scene]');
      const modalArt = scope.querySelector('[data-bmg-wall-modal-art]');
      const modalCaption = scope.querySelector('[data-bmg-wall-modal-caption]');
      const enlarge = scope.querySelector('[data-bmg-wall-enlarge]');
      const close = scope.querySelector('[data-bmg-wall-close]');
      const frameWrap = scope.querySelector('[data-bmg-frame-choice]');
      const frameSelect = scope.querySelector('[data-bmg-frame-finish]');
      const quotePurchase = document.querySelector('[data-bmg-quote-purchase]');
      const internationalFreightQuote = document.querySelector('[data-bmg-international-freight-quote]');
      const sizeEnquiry = document.querySelector('[data-bmg-size-enquiry]');
      const status = document.querySelector('[data-bmg-selection-note]');
      const tier = scope.querySelector('[data-bmg-framing-tier]');
      const delivery = document.querySelector('[data-bmg-delivery]');
      const deliveryCountry = document.querySelector('[data-bmg-delivery-country]');
      if (deliveryCountry) {
        const preferredCountry = String(data.country || 'AU').toUpperCase();
        deliveryCountry.value = Array.from(deliveryCountry.options).some(option => option.value === preferredCountry)
          ? preferredCountry : 'INTL';
      }
      const deliveryPostcodeWrap = document.querySelector('[data-bmg-delivery-postcode-wrap]');
      const deliveryPostcode = document.querySelector('[data-bmg-delivery-postcode]');
      const deliveryResult = document.querySelector('[data-bmg-delivery-result]');
      const deliveryBadge = document.querySelector('[data-bmg-delivery-badge]');
      const deliveryResultTitle = document.querySelector('[data-bmg-delivery-result-title]');
      const deliveryCopy = document.querySelector('[data-bmg-delivery-copy]');
      const deliveryTitle = document.querySelector('[data-bmg-delivery-title]');
      const deliveryAlert = document.querySelector('[data-bmg-delivery-alert]');
      const deliveryAlertCopy = document.querySelector('[data-bmg-delivery-alert-copy]');
      const sizeButtons = scope.querySelector('[data-bmg-commerce-sizes]');
      const presentationButtons = scope.querySelector('[data-bmg-commerce-presentations]');
      const frameButtons = Array.from(scope.querySelectorAll('[data-bmg-frame-finish-choice]'));
      const premiumWrap = scope.querySelector('[data-bmg-premium-style-top]');
      const premiumButtons = Array.from(scope.querySelectorAll('[data-bmg-premium-top-choice]'));
      const controller = new AbortController();
      const listen = (target, event, handler) => { if (target) target.addEventListener(event, handler, {signal: controller.signal}); };
      const current = () => model.variants.find(variant => String(variant.id) === String(select.value)) || model.variants[0];
      const defaultTier = 'standard';
      const urlTier = () => { const value = new URL(location.href).searchParams.get('framing'); return /^(standard|premium)$/.test(value || '') ? value : defaultTier; };
      if (tier) tier.value = urlTier();
      const presentationOf = variant => model.finish(variant) === 'framed' ? ((tier && tier.value === 'premium' && model.premiumAllowed) ? 'premium' : 'standard') : model.finish(variant);
      const isQuote = variant => presentationOf(variant) === 'premium';
      const label = presentation => ({paper:data.labels.paper,standard:data.labels.standardFrame,premium:data.labels.premiumFrame,acrylic:data.labels.acrylic})[presentation] || presentation;
      let premiumStyleKey =
        premiumButtons.find(button => button.getAttribute('aria-pressed') === 'true')?.dataset.bmgPremiumTopChoice ||
        document.querySelector('[data-bmg-premium-choice][aria-pressed="true"]')?.dataset.bmgPremiumChoice ||
        'black_veneer';
      const selectedPremium = () =>
        document.querySelector('[data-bmg-premium-choice="' + premiumStyleKey + '"]') ||
        document.querySelector('[data-bmg-premium-choice][aria-pressed="true"]');
      const selectedPremiumTop = () => premiumButtons.find(button => button.dataset.bmgPremiumTopChoice === premiumStyleKey);
      const shippingMoney = value => '$' + Number(value).toLocaleString('en-AU', {maximumFractionDigits:2});
      const packedWeightKg = variant => Math.max(0, Number(variant.weightGrams || 0) / 1000);
      const destinationName = () => deliveryCountry?.options?.[deliveryCountry.selectedIndex]?.textContent?.trim() || 'International';

      function deliveryPolicy(variant) {
        const country = deliveryCountry?.value || 'AU';
        const detail = model.size(variant);
        if (!detail.valid) return {state:'pending',block:true,surcharge:0,badge:'SIZE TO CONFIRM',title:'Confirm size before delivery',copy:'Delivery can be confirmed once the recorded artwork dimensions are verified.'};

        if (country !== 'AU' && model.finish(variant) !== 'paper') {
          return {state:'quote',block:true,surcharge:0,badge:'FREIGHT QUOTE REQUIRED',title:'International framed and acrylic delivery — quote first',copy:'We arrange international delivery of framed and acrylic works individually. Choose Request international freight quote below with your preferred artwork size. Any destination duties and import charges will be clarified in the written quote.'};
        }
        const weightKg = packedWeightKg(variant);
        if (country === 'AU') {
          if (weightKg <= 18) return {state:'included',block:false,surcharge:0,badge:'DELIVERY INCLUDED',title:'Australia delivery included',copy:'This selection sits within the complimentary Australia delivery band at checkout.'};
          if (weightKg <= 32) return {state:'fixed',block:false,surcharge:150,badge:'SPECIALIST DELIVERY',title:'Australia delivery — +' + shippingMoney(150) + ' AUD',copy:'Estimated delivery in AUD based on packed weight. Checkout confirms final freight, including multi-work orders.'};
          return {state:'fixed',block:false,surcharge:250,badge:'CRATED DELIVERY',title:'Australia delivery — +' + shippingMoney(250) + ' AUD',copy:'Estimated delivery in AUD based on packed weight. Checkout confirms final freight, including multi-work orders.'};
        }

        const destination = destinationName();
        return {state:'checkout',block:false,surcharge:0,badge:'PRINT DELIVERY AT CHECKOUT',title:destination + ' — unframed print delivery',copy:'Unframed prints can be purchased online. Shipping is calculated using the store’s current international print rates and confirmed at checkout. Destination import duties, taxes or fees may be charged separately.'};
      }

      function updateDelivery(variant) {
        if (!delivery) return {state:'included',block:false,surcharge:0};
        const country = deliveryCountry?.value || 'AU';
        if (deliveryPostcodeWrap) deliveryPostcodeWrap.hidden = true;
        if (deliveryPostcode) deliveryPostcode.disabled = true;
        if (deliveryAlert) deliveryAlert.hidden = true;
        const policy = deliveryPolicy(variant);
        if (deliveryResult) deliveryResult.dataset.status = policy.state;
        if (deliveryBadge) deliveryBadge.textContent = policy.badge;
        if (deliveryResultTitle) deliveryResultTitle.textContent = policy.title;
        if (deliveryCopy) deliveryCopy.textContent = policy.copy;
        if (deliveryTitle) deliveryTitle.textContent = country === 'AU' ? 'Australia delivery' : 'International delivery';
        delivery.dataset.surcharge = String(policy.surcharge || 0);
        delivery.dataset.blocked = String(policy.block);
        return policy;
      }

      function handleDeliveryCountryChange() {
        update(false, false);
      }
let lastQuoteBody = '';
      let lastSizingBody = '';
      let lastFreightBody = '';
      let observer;
      let modalTrigger;

      function orientationForArtwork() {
        if (model.aspect < 0.82) return 'portrait';
        if (model.aspect > 1.22) return 'landscape';
        return 'square';
      }
      function applyFrameState(target, scene, variant) {
        if (!target || !scene) return;
        const premium = isQuote(variant);
        const galleryFrames = {'black + black':'black','white + white':'white','natural oak':'oak'};
        const premiumFrames = {black_veneer:'black_veneer',warm_walnut:'warm_walnut',white_premium:'white_premium'};
        const galleryKey = galleryFrames[String(frameSelect?.value || 'Black + Black').toLowerCase()] || 'black';
        const premiumKey = premiumFrames[premiumStyleKey] || 'black_veneer';
        target.dataset.frame = model.finish(variant) === 'acrylic' ? 'acrylic' : model.finish(variant) === 'framed' ? (premium ? premiumKey : galleryKey) : 'none';
        const scale = Math.max(scene.clientWidth || 320, 1) / 595.24;
        const isReview = target.hasAttribute('data-bmg-review-art');
        target.style.setProperty('--wall-frame-width', Math.max((premium ? (isReview ? 11 : 9) : (isReview ? 7 : 4)) * scale, isReview ? 3.2 : 1.5) + 'px');
        target.style.setProperty('--wall-mat-width', Math.max((isReview ? 8 : 5) * scale, isReview ? 3 : 2) + 'px');
      }
      function wallStyle(target, scene, variant) {
        if (!target || !scene) return;
        const detail = model.size(variant);
        const orientation = orientationForArtwork();
        scene.dataset.orientation = orientation;
        target.dataset.orientation = orientation;
        applyFrameState(target, scene, variant);
        if (!detail.valid) return;
        const sceneWidth = Math.max(scene.clientWidth || 500, 1);
        if (orientation === 'portrait') {
          const sceneHeight = Math.max(scene.clientHeight || sceneWidth * 1.25, 1);
          const heightFraction = Math.min(0.52, Math.max(0.32, 0.22 + (detail.h * 0.0012)));
          const artworkHeight = sceneHeight * heightFraction;
          const naturalWidth = artworkHeight * (detail.w / Math.max(detail.h, 1));
          const width = Math.min(sceneWidth * 0.46, naturalWidth);
          const scaleProgress = Math.min(1, Math.max(0, (heightFraction - 0.34) / 0.18));
          const bottom = 49 - (scaleProgress * 4);
          target.style.width = Math.max(width / sceneWidth * 100, 8) + '%';
          target.style.setProperty('bottom', bottom.toFixed(2) + '%', 'important');
        } else {
          target.style.removeProperty('bottom');
          target.style.width = Math.min(detail.w / 595.24 * 100, 48) + '%';
        }
      }
      function reviewStyle(target, scene, variant) {
        if (!target || !scene) return;
        const detail = model.size(variant);
        const orientation = orientationForArtwork();
        scene.dataset.orientation = orientation;
        target.dataset.orientation = orientation;
        applyFrameState(target, scene, variant);
        const sceneWidth = Math.max(scene.clientWidth || 600, 1);
        const sceneHeight = Math.max(scene.clientHeight || (orientation === 'portrait' ? sceneWidth * 1.25 : sceneWidth * 0.75), 1);
        const ratio = detail.valid && detail.h > 0 ? detail.w / detail.h : model.aspect;
        const maxWidth = sceneWidth * (orientation === 'portrait' ? 0.56 : 0.86);
        const maxHeight = sceneHeight * (orientation === 'portrait' ? 0.78 : 0.72);
        const width = Math.min(maxWidth, maxHeight * Math.max(ratio, 0.1));
        target.style.width = Math.max(width / sceneWidth * 100, 12) + '%';
      }
      function syncWall(variant) {
        if (art) wallStyle(art, art.parentElement, variant);
        if (modal?.open) wallStyle(modalArt, modalScene, variant);
        if (reviewArt && reviewScene) reviewStyle(reviewArt, reviewScene, variant);
      }
      function setQuoteContext(variant) {
        const context = document.querySelector('[data-bmg-quote-context]');
        const field = document.querySelector('[data-bmg-quote-type]');
        const preference = selectedPremium()?.dataset.bmgQuoteStyle || '';
        const text = data.quoteLabels.context + ' · ' + data.title + ' · ' + model.size(variant).label + (preference ? ' · ' + preference : '');
        if (context) { context.hidden = false; context.textContent = text; }
        if (field) field.value = text;
        const message = document.querySelector('#ArtworkQuestion textarea');
        if (message && (!message.value || message.value === lastQuoteBody || message.value === lastSizingBody)) {
          lastQuoteBody = data.quoteLabels.intro + ' ' + data.title + ' (' + model.size(variant).label + '). ' + (preference ? preference + '. ' : '') + data.quoteLabels.prompt;
          message.value = lastQuoteBody;
        }
      }
      function setInternationalFreightContext(variant) {
        const destination = deliveryCountry?.value || String(data.country || 'US').toUpperCase();
        const presentation = model.finish(variant) === 'acrylic' ? 'Acrylic face mount' : 'Framed';
        const selection = model.size(variant).label + ' · ' + presentation;
        const text = 'International freight enquiry · ' + data.title + ' · ' + selection + ' · ' + destination;
        const context = document.querySelector('[data-bmg-quote-context]');
        const field = document.querySelector('[data-bmg-quote-type]');
        if (context) { context.hidden = false; context.textContent = text; }
        if (field) field.value = text;
        const message = document.querySelector('#ArtworkQuestion textarea');
        if (message && (!message.value.trim() || message.value === lastQuoteBody || message.value === lastSizingBody || message.value === lastFreightBody)) {
          lastFreightBody = 'I would like an international freight quote for ' + data.title + ' (' + selection + '). Delivery country: ' + destination + '. Please let me know the estimated shipping cost, packaging method, timeframe and any known import-charge arrangements. Delivery city and postcode: ';
          message.value = lastFreightBody;
        }
      }
      function openQuestion() {
        const details = document.querySelector('.bmg-artwork-question__details');
        if (details) details.open = true;
      }
      function update(push, syncPresentation) {
        const variant = current();
        const detail = model.size(variant);
        const group = model.groupOf(variant);
        const presentation = presentationOf(variant);
        const quote = isQuote(variant);
        const blocked = model.blockedBySize(variant, presentation);
        const needsSize = !detail.valid;
        const paid = typeof variant.priceCents !== 'number' || variant.priceCents > 0;
        if (price) price.textContent = quote ? data.quoteLabels.price : variant.price;
        if (collectorCredit) {
          if (quote) {
            collectorCredit.textContent = 'Earn 10% Collector Credit on the final purchase value.';
          } else {
            const creditCents = Math.round(Number(variant.priceCents || 0) / 10);
            const creditValue = creditCents / 100;
            const creditLabel = new Intl.NumberFormat('en-AU', {style:'currency',currency:'AUD',minimumFractionDigits: creditCents % 100 ? 2 : 0,maximumFractionDigits:2}).format(creditValue);
            collectorCredit.innerHTML = 'Earn <strong>' + creditLabel + '</strong> Collector Credit after fulfillment.';
          }
        }
        if (reviewSelection) reviewSelection.textContent = detail.label + ' · ' + label(presentation);
        if (reviewPrice) reviewPrice.textContent = quote ? data.quoteLabels.price : variant.price;
        if (reviewCredit && collectorCredit) reviewCredit.innerHTML = collectorCredit.innerHTML;
        if (reviewFrame) {
          const frameLabel = presentation === 'premium'
            ? (selectedPremiumTop()?.textContent?.trim() || selectedPremium()?.textContent?.trim() || '')
            : presentation === 'standard' ? (frameSelect?.value || '') : '';
          reviewFrame.textContent = frameLabel;
          reviewFrame.hidden = !frameLabel;
        }
        const deliveryState = updateDelivery(variant);
        const needsInternationalQuote = deliveryState.state === 'quote' && !needsSize && !blocked && variant.available && paid;
        if (add) {
          const baseDisabled = quote || needsSize || blocked || !variant.available || !paid;
          add.hidden = quote || needsSize || needsInternationalQuote;
          add.disabled = baseDisabled || deliveryState.block;
          if (!baseDisabled && deliveryState.block) add.textContent = deliveryState.state === 'pending' ? 'Confirm delivery to continue' : 'Freight quote required';
          else add.textContent = variant.available && !blocked ? data.labels.add : data.labels.sold;
        }
        if (internationalFreightQuote) internationalFreightQuote.hidden = !needsInternationalQuote;
        if (quotePurchase) quotePurchase.hidden = !quote || blocked || needsSize || needsInternationalQuote;
        if (sizeEnquiry) sizeEnquiry.hidden = !needsSize;
        if (frameWrap) frameWrap.hidden = presentation !== 'standard';
        if (frameSelect) frameSelect.disabled = presentation !== 'standard' || blocked || needsSize;
        if (premiumWrap) premiumWrap.hidden = presentation !== 'premium';
        if (status) {
          status.textContent = needsSize ? 'The recorded dimensions for this option need confirmation. Please ask the studio before ordering.' : blocked ? 'Framing is unavailable at this size. Select the acrylic option.' : '';
          status.hidden = !status.textContent;
        }
        sizeButtons?.querySelectorAll('button').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.sizeKey === detail.key)));
        presentationButtons?.querySelectorAll('button').forEach(button => {
          const possible = model.find(group, button.dataset.presentation, variant.id);
          button.disabled = !possible;
          button.setAttribute('aria-disabled', String(!possible));
          button.classList.toggle('is-unavailable', !possible);
          button.setAttribute('aria-pressed', String(button.dataset.presentation === presentation));
        });
        frameButtons.forEach(button => button.setAttribute('aria-pressed', String(button.dataset.bmgFrameFinishChoice === frameSelect?.value)));
        const premiumStyle = selectedPremium()?.dataset.bmgPremiumChoice || 'black_veneer';
        premiumButtons.forEach(button => button.setAttribute('aria-pressed', String(button.dataset.bmgPremiumTopChoice === premiumStyle)));
        const url = new URL(location.href);
        url.searchParams.set('variant', variant.id);
        if (model.finish(variant) === 'framed' && tier) url.searchParams.set('framing', tier.value); else url.searchParams.delete('framing');
        if (push) history.replaceState(history.state, '', url);
        document.querySelectorAll('[data-bmg-enquiry-variant]').forEach(field => field.value = detail.label + ' / ' + label(presentation));
        document.querySelectorAll('[data-bmg-enquiry-url]').forEach(field => field.value = url.origin + url.pathname + url.search);
        const context = document.querySelector('[data-bmg-quote-context]');
        if (quote && context && !context.hidden) setQuoteContext(variant);
        if (wall && art) {
          wall.hidden = false;
          art.parentElement.hidden = !detail.valid;
          if (enlarge) enlarge.disabled = !detail.valid;
          let captionText;
          if (!detail.valid) captionText = 'Size to confirm · ' + label(presentation);
          else if (detail.mismatch) captionText = 'Selected: ' + detail.label + ' · ' + label(presentation) + '. Image proportions are preserved; confirm final print dimensions.';
          else captionText = (detail.approximate ? data.labels.approx + ' ' : '') + pretty(detail.w) + ' × ' + pretty(detail.h) + ' cm · ' + label(presentation);
          if (presentation === 'standard' && frameSelect) captionText += ' · ' + frameSelect.value;
          if (caption) caption.textContent = captionText;
          if (modalCaption) modalCaption.textContent = captionText;
        }
        // Keep the final review visual in sync even on templates where the upper wall preview is absent or hidden.
        syncWall(variant);
        // Browsing lower finish samples does not change the top presentation or reset its tab.
        if (syncPresentation) document.dispatchEvent(new CustomEvent('bmg:presentation', {detail:presentation}));
      }
      sizeButtons?.replaceChildren();
      model.groups.forEach(group => {
        if (!sizeButtons) return;
        const button = document.createElement('button');
        button.type = 'button';
        button.dataset.sizeKey = group.key;
        button.textContent = group.detail.label;
        button.title = group.detail.label;
        const selectable = !!model.choose(group, presentationOf(current()), current().id);
        button.disabled = !selectable;
        button.setAttribute('aria-disabled', String(!selectable));
        button.classList.toggle('is-unavailable', !selectable);
        listen(button, 'click', () => {
          const previous = current();
          const desired = presentationOf(previous);
          const match = model.choose(group, desired, previous.id);
          if (!match) return;
          select.value = String(match.id);
          // Remember the buyer's preferred framing tier when inspecting acrylic at another size.
          update(true, true);
        });
        sizeButtons.append(button);
      });
      presentationButtons?.replaceChildren();
      const choices = [];
      if (model.variants.some(variant => model.finish(variant) === 'paper')) choices.push('paper');
      if (model.variants.some(variant => model.finish(variant) === 'framed')) { choices.push('standard'); if (model.premiumAllowed) choices.push('premium'); }
      if (model.variants.some(variant => model.finish(variant) === 'acrylic')) choices.push('acrylic');
      choices.forEach(presentation => {
        if (!presentationButtons) return;
        const button = document.createElement('button');
        button.type = 'button'; button.dataset.presentation = presentation; button.textContent = label(presentation);
        listen(button, 'click', () => {
          const match = model.find(model.groupOf(current()), presentation, current().id);
          if (!match || button.disabled) return;
          if (tier && /^(standard|premium)$/.test(presentation)) tier.value = presentation;
          select.value = String(match.id);
          update(true, true);
        });
        presentationButtons.append(button);
      });
      // Keep the native Shopify variant IDs, while giving accessibility/enquiry text metric labels.
      if (select.options) Array.from(select.options).forEach(option => {
        const variant = model.variants.find(item => String(item.id) === option.value);
        if (variant) option.textContent = model.size(variant).label + ' / ' + label(model.finish(variant) === 'framed' ? 'standard' : model.finish(variant));
      });
      frameButtons.forEach(button => listen(button, 'click', () => {
        if (!frameSelect) return;
        frameSelect.value = button.dataset.bmgFrameFinishChoice;
        frameSelect.dispatchEvent(new Event('change', {bubbles:true}));
      }));
      listen(frameSelect, 'change', () => update(false, false));
      premiumButtons.forEach(button => listen(button, 'click', () => {
        premiumStyleKey = button.dataset.bmgPremiumTopChoice || 'black_veneer';
        premiumButtons.forEach(option => option.setAttribute('aria-pressed', String(option.dataset.bmgPremiumTopChoice === premiumStyleKey)));
        const lower = document.querySelector('[data-bmg-premium-choice="' + premiumStyleKey + '"]');
        if (lower && lower.getAttribute('aria-pressed') !== 'true') lower.click();
        update(false, false);
      }));
      listen(document, 'bmg:premium-style', event => {
        if (event.detail) premiumStyleKey = String(event.detail);
        premiumButtons.forEach(option => option.setAttribute('aria-pressed', String(option.dataset.bmgPremiumTopChoice === premiumStyleKey)));
        update(false, false);
      });
      listen(select, 'change', () => update(true, true));
      listen(deliveryCountry, 'change', handleDeliveryCountryChange);
      listen(deliveryPostcode, 'input', () => {
        if (deliveryPostcode) deliveryPostcode.value = deliveryPostcode.value.replace(/\D/g, '').slice(0, 4);
        update(false, false);
      });
      listen(enlarge, 'click', () => {
        if (!modal || !model.size(current()).valid) return;
        modalTrigger = document.activeElement;
        if (typeof modal.showModal === 'function') modal.showModal(); else modal.setAttribute('open', '');
        requestAnimationFrame(() => syncWall(current()));
      });
      const closeModal = () => { if (modal?.close) modal.close(); else modal?.removeAttribute('open'); };
      listen(close, 'click', closeModal);
      listen(modal, 'click', event => { if (event.target === modal) closeModal(); });
      listen(modal, 'close', () => modalTrigger?.focus({preventScroll:true}));
      if (typeof ResizeObserver !== 'undefined' && art) {
        observer = new ResizeObserver(() => syncWall(current()));
        observer.observe(art.parentElement);
        if (modalScene) observer.observe(modalScene);
        if (reviewScene) observer.observe(reviewScene);
      }
      listen(window, 'popstate', () => {
        const id = new URL(location.href).searchParams.get('variant');
        if (tier) tier.value = urlTier();
        if (id && model.variants.some(variant => String(variant.id) === id)) select.value = id;
        update(false, true);
      });
      document.querySelectorAll('a[href="#artwork-question"]').forEach(link => listen(link, 'click', openQuestion));
      document.querySelectorAll('[data-bmg-premium-quote]').forEach(link => listen(link, 'click', () => {
        setQuoteContext(current()); openQuestion();
        document.querySelector('#ArtworkQuestion [name="contact[name]"]')?.focus({preventScroll:true});
      }));
      listen(internationalFreightQuote, 'click', () => {
        openQuestion();
        setInternationalFreightContext(current());
        document.querySelector('#ArtworkQuestion [name="contact[name]"]')?.focus({preventScroll:true});
      });
      listen(sizeEnquiry, 'click', () => {
        openQuestion();
        const message = document.querySelector('#ArtworkQuestion textarea');
        if (message && (!message.value || message.value === lastQuoteBody || message.value === lastSizingBody)) {
          lastSizingBody = 'Please confirm the dimensions and availability of the largest size option for ' + data.title + '.';
          message.value = lastSizingBody;
        }
        const context = document.querySelector('[data-bmg-quote-context]');
        if (context) { context.hidden = false; context.textContent = 'Sizing enquiry · ' + data.title; }
        const field = document.querySelector('[data-bmg-quote-type]');
        if (field) field.value = 'Sizing enquiry · ' + data.title;
      });
      listen(form, 'submit', event => {
        const variant = current();
        if (isQuote(variant)) { event.preventDefault(); quotePurchase?.click(); return; }
        const deliveryState = deliveryPolicy(variant);
        if (deliveryState.block) {
          event.preventDefault();
          if (deliveryState.state === 'quote') internationalFreightQuote?.click();
          else delivery?.scrollIntoView({behavior:'smooth',block:'center'});
          return;
        }
        if (!model.size(variant).valid || model.blockedBySize(variant, presentationOf(variant)) || !variant.available || Number(variant.priceCents) === 0) {
          event.preventDefault();
          if (!model.size(variant).valid) sizeEnquiry?.click();
        }
      });
      listen(document, 'shopify:section:unload', event => {
        if (event.target === scope || event.target.contains(dataNode)) { observer?.disconnect(); controller.abort(); delete dataNode.dataset.ready; }
      });
      let corrected = false;
      let initial = current();
      const explicitVariant = new URL(location.href).searchParams.has('variant');
      if (!explicitVariant && (data.edition === 'limited' || data.edition === 'exclusive') && model.finish(initial) !== 'framed') {
        const preferred = model.find(model.groupOf(initial), defaultTier, initial.id);
        if (preferred) {
          if (tier) tier.value = defaultTier;
          select.value = String(preferred.id);
          initial = current();
          corrected = true;
        }
      }
      if (model.blockedBySize(initial, presentationOf(initial))) {
        const replacement = model.find(model.groupOf(initial), 'acrylic');
        if (replacement) { select.value = String(replacement.id); corrected = true; }
      }
      root?.setAttribute('data-bmg-ready', 'true');
      update(corrected, false);
      requestAnimationFrame(() => update(false, true));
    });
  }
  window.BMGPrintConfigurator = {version:VERSION, init};
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
  document.addEventListener('shopify:section:load', init);
})();

